// src/components/dashboard/RecentReservationsCard.js
// 119 營運總覽「新進訂單」
//
// 為什麼存在：Web Push 點通知在部分裝置只會跳到首頁、不會跳到訂位那一天
// （伺服器端已排除，變因在裝置端）。所以首頁要有一條「一定找得到」的路 ——
// 進來就看到最新進的訂位，點一下直接跳到那天並標出那一筆。
//
// 顯示規則（桌號寫法、稱謂、狀態顏色、字級）全部沿用 utils/reservation-display，
// 與訂單管理頁同一套，不要在這裡重新定義。
//
// 互動：整列 / 整張卡都可點（不是只有一顆小按鈕）—— 與訂單管理的列一致。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import { useNavigate } from 'react-router-dom';
import restaurantService from '../../services/restaurantService';
import { loadStoredFontScale, FONT_SCALE_EVENT } from '../Layout';
import { getPlanSetting } from '../../utils/features';
import {
  fmtTime,
  fmtDateWithWeekday,
  fmtRelativeTime,
  minutesSince,
  formatDisplayName,
  StatusDot,
  SourceChip,
  renderExtraBadges,
  tableChipLabel,
  tableFullLabel,
  RECENT_HIGHLIGHT_MINUTES,
  NEW_ROW_BG,
  scaleToCardFs,
  scaleToRecentCellFs,
} from '../../utils/reservation-display';

const LIMIT = 30;

// 近 1 小時進來的整列淡藍底 —— 一眼看出哪些是剛進來的
const isNew = (r) => {
  const m = minutesSince(r.created_at);
  return m !== null && m < RECENT_HIGHLIGHT_MINUTES;
};

const RecentReservationsCard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 字級跟著 navbar 全域「A」走（與訂單管理同一個事件來源）
  const [fontScale, setFontScale] = useState(() => loadStoredFontScale());
  useEffect(() => {
    const onScale = (e) => setFontScale(typeof e?.detail === 'number' ? e.detail : loadStoredFontScale());
    window.addEventListener(FONT_SCALE_EVENT, onScale);
    return () => window.removeEventListener(FONT_SCALE_EVENT, onScale);
  }, []);
  const cellFs = scaleToRecentCellFs(fontScale); // 桌機 cell（18 / 21 / 24）
  const fs = scaleToCardFs(fontScale);           // 手機卡片基準（20 / 22 / 24）— 同訂單管理

  const headerSx = useMemo(() => ({
    bgcolor: '#f5f5f5', fontWeight: 700, fontSize: Math.max(13, cellFs - 4), color: '#666',
    position: 'sticky', top: 0, zIndex: 2, borderBottom: '1px solid #e0e0e0',
    whiteSpace: 'nowrap', py: 1,
  }), [cellFs]);
  const cellSx = useMemo(() => ({ fontSize: cellFs, py: 1.5, whiteSpace: 'nowrap' }), [cellFs]);

  // 加購項目設定：與訂單管理頁同一個來源（plan_settings，不需額外打 API）
  const addonConfig = useMemo(() => {
    const v = getPlanSetting('reservationAddons', []);
    return Array.isArray(v) ? v : [];
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await restaurantService.getRecentReservations(LIMIT);
      if (body?.success && body.data) {
        setRows(Array.isArray(body.data.reservations) ? body.data.reservations : []);
      } else {
        setError(body?.error || '載入新進訂單失敗');
      }
    } catch (e) {
      setError('載入新進訂單失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // 點整列/整卡 → 跳到該筆訂位的日期，並把那一筆標出來。
  // 推播的網址帶的是同一組參數（backend notifyStaffOfNewReservation），兩條路共用同一套邏輯。
  const openReservation = useCallback((r) => {
    navigate(`/restaurant/reservations?date=${r.reservationDate}&highlight=${encodeURIComponent(r.id)}`);
  }, [navigate]);

  const renderEmptyOrError = () => {
    if (error) {
      return (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography sx={{ color: '#999', mb: 1.5, fontSize: cellFs }}>{error}</Typography>
          <Button variant="outlined" onClick={fetchRows}>重新載入</Button>
        </Box>
      );
    }
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <RestaurantMenuIcon sx={{ fontSize: 44, color: '#ddd', mb: 1 }} />
        <Typography sx={{ color: '#999', fontSize: cellFs }}>目前還沒有訂位</Typography>
      </Box>
    );
  };

  // ── 桌機：表格（整列可點）──────────────────────────────────────
  const renderTable = () => (
    <TableContainer sx={{ maxHeight: 520, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx}>進來時間</TableCell>
            <TableCell sx={headerSx}>日期</TableCell>
            <TableCell sx={headerSx}>時間</TableCell>
            <TableCell sx={headerSx}>桌號</TableCell>
            <TableCell sx={headerSx}>姓名</TableCell>
            <TableCell sx={headerSx} align="right">人數</TableCell>
            <TableCell sx={headerSx}>電話</TableCell>
            <TableCell sx={headerSx}>備註 / 附加</TableCell>
            <TableCell sx={headerSx}>狀態</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && rows.length === 0 && Array.from({ length: 6 }).map((_, i) => (
            <TableRow key={`sk-${i}`}>
              <TableCell colSpan={9}><Skeleton variant="text" height={30} /></TableCell>
            </TableRow>
          ))}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} sx={{ borderBottom: 'none' }}>{renderEmptyOrError()}</TableCell>
            </TableRow>
          )}
          {rows.map((r) => {
            const badges = renderExtraBadges(r.extras, addonConfig, cellFs - 5);
            return (
              <TableRow
                key={r.id}
                onClick={() => openReservation(r)}
                sx={{
                  cursor: 'pointer',
                  ...(isNew(r) ? { bgcolor: NEW_ROW_BG } : {}),
                  '&:hover': { bgcolor: '#f0f4ff !important' },
                }}
              >
                <TableCell sx={{ ...cellSx, color: '#999', fontSize: cellFs - 4 }}>{fmtRelativeTime(r.created_at)}</TableCell>
                <TableCell sx={cellSx}>{fmtDateWithWeekday(r.reservationDate)}</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>{fmtTime(r.reservationTime)}</TableCell>
                <TableCell sx={cellSx}>
                  {tableChipLabel(r) === '候補' ? (
                    <Typography sx={{ fontSize: cellFs - 1, color: '#ff9800', fontWeight: 500 }}>候補</Typography>
                  ) : (
                    <Typography title={tableFullLabel(r)} sx={{ fontSize: cellFs, fontWeight: 500 }}>{tableChipLabel(r)}</Typography>
                  )}
                </TableCell>
                <TableCell sx={cellSx}>
                  {formatDisplayName(r.customerName, r.customerGender)}
                  <SourceChip source={r.source} />
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  <Typography component="span" sx={{ fontSize: cellFs + 6, fontWeight: 700, lineHeight: 1 }}>{r.partySize}</Typography>
                </TableCell>
                <TableCell sx={cellSx}>{r.customerPhone || ''}</TableCell>
                <Tooltip title={r.customerNote || ''} placement="top" disableHoverListener={!r.customerNote}>
                  <TableCell sx={{ ...cellSx, maxWidth: 240, whiteSpace: 'normal' }}>
                    {badges}
                    {(r.customerNote || !badges) && (
                      <Typography sx={{
                        fontSize: cellFs - 4, color: r.customerNote ? '#666' : '#bbb', mt: badges ? 0.5 : 0,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 230,
                      }}>
                        {r.customerNote || '無附加項目'}
                      </Typography>
                    )}
                  </TableCell>
                </Tooltip>
                <TableCell sx={cellSx}><StatusDot status={r.status} size={cellFs - 2} /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ── 手機：卡片列表（整張可點）──────────────────────────────────
  // 字級沿用訂單管理的手機卡片基準（fs = 20/22/24），比原本的 15px 大一輪。
  const renderCards = () => {
    if (loading && rows.length === 0) {
      return Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={`sk-${i}`} variant="rounded" height={128} sx={{ borderRadius: '14px', mb: 1.25 }} />
      ));
    }
    if (rows.length === 0) return renderEmptyOrError();
    return (
      <Box sx={{ maxHeight: 560, overflow: 'auto' }}>
        {rows.map((r) => {
          const badges = renderExtraBadges(r.extras, addonConfig, fs - 8);
          const waitlist = tableChipLabel(r) === '候補';
          return (
            <Box
              key={r.id}
              onClick={() => openReservation(r)}
              sx={{
                cursor: 'pointer',
                border: '1px solid', borderColor: isNew(r) ? '#bcd9f2' : '#e0e0e0',
                bgcolor: isNew(r) ? NEW_ROW_BG : '#fff',
                borderLeft: `4px solid ${waitlist ? '#ff9800' : '#145da0'}`,
                borderRadius: '14px', px: 1.5, py: 1.75, mb: 1.25,
                boxShadow: '0 1px 3px rgba(15,43,74,0.08)',
                '&:active': { bgcolor: '#f0f4ff' },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Typography sx={{ fontSize: fs, fontWeight: 800, color: '#0f2b4a' }}>
                  {fmtDateWithWeekday(r.reservationDate)} {fmtTime(r.reservationTime)}
                </Typography>
                <Typography sx={{ fontSize: fs - 8, color: '#94a3b8', whiteSpace: 'nowrap', pt: 0.5 }}>
                  {fmtRelativeTime(r.created_at)}
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Typography sx={{ fontSize: fs, fontWeight: 700 }}>
                  {formatDisplayName(r.customerName, r.customerGender)}
                </Typography>
                <Typography component="span" sx={{ fontSize: fs, fontWeight: 800, color: '#e65100' }}>{r.partySize}</Typography>
                <Typography component="span" sx={{ fontSize: fs - 6, color: '#64748b' }}>位</Typography>
                <SourceChip source={r.source} />
              </Stack>

              <Typography sx={{ fontSize: fs - 2, fontWeight: 600, color: '#334155', mt: 0.5 }}>
                {r.customerPhone || '—'}
              </Typography>

              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Box title={tableFullLabel(r)} sx={{
                  fontSize: fs - 2, fontWeight: 800, px: 1.25, py: '2px', borderRadius: '8px', whiteSpace: 'nowrap',
                  bgcolor: waitlist ? '#fff3e0' : '#e8f0f9', color: waitlist ? '#e65100' : '#145da0',
                }}>
                  {tableChipLabel(r)}
                </Box>
                <StatusDot status={r.status} size={fs - 6} />
              </Stack>

              {(badges || r.customerNote) && (
                <Box sx={{ mt: 0.75 }}>
                  {badges}
                  {r.customerNote && (
                    <Typography sx={{ fontSize: fs - 6, color: '#666', mt: badges ? 0.5 : 0 }}>{r.customerNote}</Typography>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  // 手機：外框拿掉、左右內距歸零 —— 列本身已經是卡片了，再包一層外框會變成
  // 「卡中卡」，而且 Layout 24 + Dashboard + CardContent 疊起來會讓列卡片只剩螢幕 7 成寬。
  // 桌機維持有框的卡片（裡面是表格不是卡片）。
  return (
    <Card
      variant="outlined"
      sx={{
        mt: 3,
        ...(isMobile ? { border: 'none', bgcolor: 'transparent', boxShadow: 'none' } : {}),
      }}
    >
      <CardContent sx={{ px: { xs: 0, md: 2 }, '&:last-child': { pb: { xs: 1, md: 3 } } }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 1, flexWrap: 'wrap', mb: 1.5,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: (isMobile ? fs : cellFs) + 2, fontWeight: 700 }}>新進訂單</Typography>
            <Box component="span" sx={{
              bgcolor: '#e8f1fa', color: '#0f4a80', borderRadius: '11px',
              px: 1.1, py: '1px', fontSize: (isMobile ? fs : cellFs) - 5, fontWeight: 700,
            }}>
              最新 {LIMIT} 筆
            </Box>
          </Stack>
          <Typography sx={{ fontSize: (isMobile ? fs : cellFs) - 5, color: '#999' }}>
            點任一筆 → 跳到那天並標出來
          </Typography>
        </Box>

        {isMobile ? renderCards() : renderTable()}
      </CardContent>
    </Card>
  );
};

export default RecentReservationsCard;
